import { effect, inject, Injectable, signal } from '@angular/core';
import { ThemeService } from './shared/utils/services/theme.service';
import { InstallService } from './shared/utils/services/install.service';
import { LanguageService } from './shared/utils/services/language.service';
import { DeviceService } from './shared/services/device.service';
import { App } from '@capacitor/app';
import { TranslocoService } from '@jsverse/transloco';
import { AccountsAppService } from './shared/services/core/accounts-app.service';
import { OrgService } from './shared/services/org.service';
import { UserService } from './shared/services/user.service';
import { Router } from '@angular/router';
import { OrgPaymentsService } from './shared/services/payments.service';

@Injectable({
  providedIn: 'root',
})
export class AppService {
  private deviceService = inject(DeviceService);
  private themeService = inject(ThemeService);
  private installService = inject(InstallService);
  private languageService = inject(LanguageService);
  private translocoService = inject(TranslocoService);
  private accountsAppService = inject(AccountsAppService);

  private userService = inject(UserService);
  private orgService = inject(OrgService);
  private orgPaymentsService = inject(OrgPaymentsService);

  private router = inject(Router);

  loading = signal<boolean>(false);

  authStateEffect = effect(async () => {
    if (
      this.accountsAppService.isLoggedIn() &&
      !this.accountsAppService.loading()
    ) {
      // this.authStateEffect.destroy();
      // console.log('AUTH_STATE_EFFECT');
      // Init AccountsApp Service
      this.loading.set(true);
      await this.initApp();
    }
  });

  constructor() {
    // console.log('INIT');
  }

  async init() {
    // Theme Initialization
    this.themeService.init();

    // Install Service
    this.installService.start();

    // Initialize Languages
    await this.languageService.init();

    // Device Initialization
    this.deviceService.init();
  }

  /**
   * Initializes the application by setting the loading state and fetching user and organization data.
   *
   * If the user is successfully initialized, it attempts to initialize organizations.
   * Navigates the user to the appropriate onboarding page based on organization data.
   * If a single organization exists and is active, it sets it as active.
   * Handles the loading state throughout the initialization process.
   */

  async initApp() {
    const user = await this.userService.init();
    if (!!user) {
      const orgs = await this.orgService.init();
      const activeOrgId = localStorage.getItem('activeOrgId');
      if (orgs && orgs?.length > 0) {
        // Clear the onboarding_pending flag now that AppService is handling routing based on org data.
        localStorage.removeItem('onboarding_pending');
        const isOneOfOrgs = orgs.some((org: any) => org.id === activeOrgId);
        if (activeOrgId && isOneOfOrgs) {
          this.loading.set(true);
          // console.log('Setting active org:', activeOrgId);
          // await new Promise((resolve) => setTimeout(resolve, 3000));
          await this.orgService.setActiveOrg(activeOrgId);
          await this.orgPaymentsService.getOrgPaymentAccounts();
          this.loading.set(false);
          return;
        }

        if (orgs.length === 1) {
          await this.orgService.setActiveOrg(orgs[0].id!, false);
          this.loading.set(false);
          return;
        }

        await this.router.navigateByUrl('/onboarding/choose-org');
        this.loading.set(false);
        return;
      }

      // Clear the onboarding_pending flag now that AppService is handling routing based on org data.
      localStorage.removeItem('onboarding_pending');
      if (orgs === null || orgs?.length === 0) {
        await this.router.navigateByUrl('/onboarding');
        this.loading.set(false);
        return;
      }
    } else {
      this.loading.set(false);
      return;
    }
  }

  installApp() {
    this.installService.installApp();
  }

  /**
   * Listen to the back button event and handle the app's back button behavior
   *
   * If the user is not on the home page and there is a previous page in the history,
   * the user is taken back to the previous page. Otherwise, the user is prompted to
   * confirm that they want to exit the app. If the user confirms, the app exits.
   */
  backBtnListener() {
    App.addListener('backButton', (event) => {
      // Handle the back button behavior here
      if (
        window.location.pathname !== '/home' &&
        history.state.navigationId > 1
      ) {
        window.history.back();
      } else {
        const value = this.translocoService.translate('exit_text');
        const confirm = window.confirm(value);
        if (confirm) {
          App.exitApp();
        }
      }
    });
  }
}
